import React, { useEffect, useState } from "react";
import {
  Modal,
  Box,
  FormControlLabel,
  Switch,
  Typography,
  Paper,
  IconButton,
  Button,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import * as Yup from "yup";
import { Formik, Form } from "formik";
import { InputField } from "../../../pro/applicationForm/input";
import { CustomSelectField } from "../../../pro/applicationForm/customSelect";
import axiosInstant from "../../../../api";

const validationSchema = Yup.object().shape({
  name: Yup.string().required("Name is required"),
  content_channel_id: Yup.string().required("Channel is required"),
});

// Placements are the specific catalog entries (e.g. "Amazon PDP Video"),
// each mapped to one of the 6 generic content channels.
const ContentPlacementModal = ({ open, onClose, onSave, initialData = {} }) => {
  const [categories, setCategories] = useState([]);
  const [channels, setChannels] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [loadingChannels, setLoadingChannels] = useState(false);

  const fetchCategories = async () => {
    setLoadingCategories(true);
    try {
      const response = await axiosInstant.get("/content-placement-categories");
      const categoryData = Array.isArray(response.data)
        ? response.data
        : response.data?.rows || [];
      setCategories(categoryData);
    } catch (err) {
      console.error("Failed to fetch placement categories:", err);
      setCategories([]);
    } finally {
      setLoadingCategories(false);
    }
  };

  const fetchChannels = async () => {
    setLoadingChannels(true);
    try {
      const response = await axiosInstant.get("/content-channels");
      const channelData = Array.isArray(response.data)
        ? response.data
        : response.data?.rows || [];
      setChannels(channelData);
    } catch (err) {
      console.error("Failed to fetch content channels:", err);
      setChannels([]);
    } finally {
      setLoadingChannels(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchCategories();
      fetchChannels();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const categoryOptions = categories.map((cat) => ({
    label: cat.name,
    value: cat.id,
  }));

  const channelOptions = channels.map((ch) => ({
    label: ch.name,
    value: ch.id,
  }));

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        component={Paper}
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: { xs: "calc(100% - 24px)", sm: 480 },
          maxWidth: 480,
          maxHeight: { xs: "calc(100vh - 24px)", sm: "85vh" },
          bgcolor: "white",
          borderRadius: "12px",
          boxShadow: 24,
          padding: { xs: "16px", sm: "24px" },
          overflow: "auto",
        }}
      >
        <Formik
          validationSchema={validationSchema}
          initialValues={{
            name: initialData.name || "",
            content_channel_id:
              initialData.content_channel?.id ||
              initialData.content_channel_id ||
              "",
            content_placement_category_id:
              initialData.category?.id ||
              initialData.content_placement_category_id ||
              "",
            autopilot_available: initialData.autopilot_available !== false,
          }}
          enableReinitialize
          onSubmit={async (values, { setSubmitting }) => {
            try {
              await onSave({
                ...values,
                content_placement_category_id:
                  values.content_placement_category_id || null,
              });
            } finally {
              setSubmitting(false);
            }
          }}
        >
          {({ isValid, isSubmitting, values, setFieldValue }) => (
            <Form className="flex flex-col gap-4">
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography sx={{ fontSize: "24px", fontWeight: 900 }}>
                  {initialData?.id ? "Edit" : "Add"} Content Placement
                </Typography>
                <IconButton onClick={onClose}>
                  <CloseIcon />
                </IconButton>
              </Box>

              <InputField
                name="name"
                placeholder="Enter placement name"
                label="Name"
                required
                type="text"
              />

              {loadingChannels ? (
                <Box display="flex" justifyContent="center" py={2}>
                  <CircularProgress size={24} />
                </Box>
              ) : (
                <CustomSelectField
                  name="content_channel_id"
                  label="Generic Channel"
                  required
                  options={channelOptions}
                  placeholder="Select Channel"
                />
              )}

              {loadingCategories ? (
                <Box display="flex" justifyContent="center" py={2}>
                  <CircularProgress size={24} />
                </Box>
              ) : (
                <CustomSelectField
                  name="content_placement_category_id"
                  label="Placement Category"
                  required={false}
                  options={categoryOptions}
                  placeholder="Select Category"
                />
              )}

              <FormControlLabel
                control={
                  <Switch
                    checked={values.autopilot_available}
                    onChange={(e) => setFieldValue("autopilot_available", e.target.checked)}
                    color="primary"
                  />
                }
                label="Available for Autopilot"
              />

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 2,
                  mt: 1,
                }}
              >
                <Button onClick={onClose} type="button">
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={isSubmitting || !isValid}
                >
                  {initialData?.id ? "Save" : "Add"}
                </Button>
              </Box>
            </Form>
          )}
        </Formik>
      </Box>
    </Modal>
  );
};

export default ContentPlacementModal;
